import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';

import { ClickAwayListener, makeStyles } from '@material-ui/core';
import { alpha } from '@material-ui/core/styles';
import PropTypes from 'prop-types';

import { Mui } from 'components';

const OVERLAY_Z_INDEX = 1600;
const ARM_DELAY = 2000;
const HOLE_PADDING = 6;

const useStyles = makeStyles(theme => ({
  panel: {
    position: 'fixed',
    backgroundColor: alpha(theme.palette.common.black, 0.5),
    zIndex: OVERLAY_Z_INDEX,
  },
  ring: {
    position: 'fixed',
    border: `2px solid ${theme.palette.primary.main}`,
    borderRadius: theme.shape.borderRadius,
    boxSizing: 'border-box',
    pointerEvents: 'none',
    zIndex: OVERLAY_Z_INDEX,
  },
  box: {
    position: 'fixed',
    right: theme.spacing(4),
    bottom: theme.spacing(4),
    width: 460,
    maxWidth: 'calc(100vw - 64px)',
    padding: theme.spacing(4),
    zIndex: OVERLAY_Z_INDEX,
  },
  header: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
  },
  body: {
    marginTop: theme.spacing(1.5),
  },
  actions: {
    marginTop: theme.spacing(3),
    display: 'flex',
    justifyContent: 'flex-end',
  },
}));

const isInViewport = rect =>
  rect.top >= 0 && rect.bottom <= (window.innerHeight || document.documentElement.clientHeight);

function useTrackedRect(element) {
  const [rect, setRect] = useState(null);

  useEffect(() => {
    if (!element) {
      setRect(null);
      return undefined;
    }

    let raf = 0;
    const measure = () => {
      raf = 0;
      setRect(element.getBoundingClientRect());
    };
    const schedule = () => {
      if (!raf) raf = window.requestAnimationFrame(measure);
    };
    const ensureVisible = () => {
      if (!isInViewport(element.getBoundingClientRect())) {
        element.scrollIntoView({ block: 'center', behavior: 'smooth' });
      }
    };

    measure();
    ensureVisible();
    const settlePoll = window.setInterval(() => {
      schedule();
      ensureVisible();
    }, 200);
    const settleStop = window.setTimeout(() => window.clearInterval(settlePoll), ARM_DELAY);

    const io = new IntersectionObserver(() => schedule(), { threshold: 0 });
    io.observe(element);

    let ro;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(schedule);
      ro.observe(element);
    }

    document.addEventListener('scroll', schedule, true);
    window.addEventListener('resize', schedule);

    return () => {
      window.clearInterval(settlePoll);
      window.clearTimeout(settleStop);
      io.disconnect();
      if (ro) ro.disconnect();
      document.removeEventListener('scroll', schedule, true);
      window.removeEventListener('resize', schedule);
      if (raf) window.cancelAnimationFrame(raf);
    };
  }, [element]);

  return rect;
}

function useArmed(element) {
  const [armed, setArmed] = useState(false);
  useEffect(() => {
    setArmed(false);
    const timer = window.setTimeout(() => setArmed(true), ARM_DELAY);
    return () => window.clearTimeout(timer);
  }, [element]);
  return armed;
}

function useEscapeToDismiss(onAdvance) {
  useEffect(() => {
    const onKeyDown = event => {
      if (event.key === 'Escape') onAdvance();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onAdvance]);
}

function SpotlightCutout({ rect, classes }) {
  const hole = {
    top: Math.max(rect.top - HOLE_PADDING, 0),
    left: Math.max(rect.left - HOLE_PADDING, 0),
    width: rect.width + HOLE_PADDING * 2,
    height: rect.height + HOLE_PADDING * 2,
  };
  const panels = [
    { key: 'top', style: { top: 0, left: 0, right: 0, height: hole.top } },
    { key: 'bottom', style: { top: hole.top + hole.height, left: 0, right: 0, bottom: 0 } },
    { key: 'left', style: { top: hole.top, left: 0, width: hole.left, height: hole.height } },
    {
      key: 'right',
      style: { top: hole.top, left: hole.left + hole.width, right: 0, height: hole.height },
    },
  ];

  return (
    <>
      {panels.map(panel => (
        <div key={panel.key} className={classes.panel} style={panel.style} />
      ))}
      <div
        className={classes.ring}
        style={{ top: hole.top, left: hole.left, width: hole.width, height: hole.height }}
      />
    </>
  );
}

SpotlightCutout.propTypes = {
  rect: PropTypes.object.isRequired,
  classes: PropTypes.object.isRequired,
};

function SpotlightOverlay({ hint, element, hasNext, onAdvance }) {
  const classes = useStyles();
  const rect = useTrackedRect(element);
  const armed = useArmed(element);

  useEscapeToDismiss(onAdvance);

  const handleClickAway = () => {
    if (armed) onAdvance();
  };

  return ReactDOM.createPortal(
    <>
      {rect && <SpotlightCutout rect={rect} classes={classes} />}
      <ClickAwayListener onClickAway={handleClickAway}>
        <Mui.Paper elevation={8} className={classes.box}>
          <div className={classes.header}>
            <Mui.Typography variant="h6">
              <strong>{hint.title}</strong>
            </Mui.Typography>
            <Mui.IconButton onClick={onAdvance} aria-label="Fechar">
              <Mui.Icon>close</Mui.Icon>
            </Mui.IconButton>
          </div>
          <Mui.Typography variant="body1" color="textSecondary" className={classes.body}>
            {hint.body}
          </Mui.Typography>
          <div className={classes.actions}>
            <Mui.Button variant="contained" color="primary" onClick={onAdvance}>
              {hint.actionLabel || (hasNext ? 'Próximo' : 'Entendi')}
            </Mui.Button>
          </div>
        </Mui.Paper>
      </ClickAwayListener>
    </>,
    document.body,
  );
}

SpotlightOverlay.propTypes = {
  hint: PropTypes.shape({
    title: PropTypes.string,
    body: PropTypes.string,
    actionLabel: PropTypes.string,
  }).isRequired,
  element: PropTypes.object,
  hasNext: PropTypes.bool,
  onAdvance: PropTypes.func.isRequired,
};

export default SpotlightOverlay;
